import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ListingService from '../services/ListingService';

const getAreaCounts = (area, listings) => {
  if (area && area.forSaleCount !== undefined && area.forSaleCount !== null && area.forRentCount !== undefined && area.forRentCount !== null) {
    return { sale: area.forSaleCount, rent: area.forRentCount };
  }

  const title = (area?.title || '').trim().toLowerCase();
  const slug = title.replace(/\s+/g, '-');
  
  let sale = 0;
  let rent = 0;

  if (listings && Array.isArray(listings)) {
    listings.forEach((l) => {
      if (!l || !l.area) return;
      const lArea = l.area.trim().toLowerCase();
      const lSlug = lArea.replace(/\s+/g, '-');
      if (lArea === title || lSlug === slug || lArea.includes(title) || title.includes(lArea)) {
        const type = (l.listingType || '').toUpperCase();
        if (type.includes('RENT')) {
          rent++;
        } else {
          sale++;
        }
      }
    });
  }

  return { sale, rent };
};

const AllAreasPage = ({ areas, listings, agent, onGoBack }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAreaClick = (area) => {
    const slug = area.title.toLowerCase().replace(/\s+/g, '-');
    navigate(`/area/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredAreas = (areas || []).filter(area => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const matchTitle = area.title && area.title.toLowerCase().includes(q);
    const matchDesc = area.description && area.description.toLowerCase().includes(q);
    const matchHighlights = area.highlights && area.highlights.some(h => h.toLowerCase().includes(q));
    return matchTitle || matchDesc || matchHighlights;
  });

  return (
    <div className="all-areas-page">
      {/* Hero Header Section */}
      <div 
        className="area-hero-section" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(9, 13, 22, 0.85), rgba(16, 24, 38, 0.9)), url(https://images.unsplash.com/photo-1577495508048-b635879837f1?auto=format&fit=crop&w=1920&q=80)` 
        }}
      >
        <div className="area-hero-content">
          <span className="area-hero-tag">JOHOR BAHRU & ISKANDAR PUTERI</span>
          <h1 className="area-hero-title">EXPLORE ALL POPULAR AREAS</h1>
          <p className="area-hero-desc">
            Discover prime residential townships, gated communities, and luxury lifestyle hubs managed by Nicole Pua Property Consultancy.
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className="area-container">
        {/* Breadcrumb navigation */}
        <div className="breadcrumbs">
          <span className="breadcrumb-item" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>
            Home
          </span>
          <span className="breadcrumb-separator"><i className="bi bi-chevron-right"></i></span>
          <span className="breadcrumb-item active">All Popular Areas</span>
        </div>

        <div className="area-page-header">
          <div>
            <h2 className="properties-section-title">ALL TOWNSHIPS & COMMUNITIES</h2>
            <p className="properties-section-subtitle">
              Select an area to view available sale & rental listings, township guides, and market insights.
            </p>
          </div>
          <span className="properties-found-count">
            {filteredAreas.length} {filteredAreas.length === 1 ? 'Area' : 'Areas'} Available
          </span>
        </div>

        {/* Client-side Search Township Filter Bar */}
        <div className="township-search-container">
          <i className="bi bi-search township-search-icon"></i>
          <input 
            type="text"
            className="township-search-input"
            placeholder="Search township name or highlight (e.g. Bukit Indah, Horizon Hills, Eco Botanic, School...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="township-search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
              aria-label="Clear search"
            >
              <i className="bi bi-x-circle-fill"></i>
            </button>
          )}
        </div>

        {/* Areas Grid or Empty State */}
        {filteredAreas.length === 0 ? (
          <div className="township-empty-state">
            <div className="township-empty-icon">
              <i className="bi bi-search"></i>
            </div>
            <div className="township-empty-title">No Townships Found</div>
            <p>No townships or communities match "{searchQuery}". Try resetting your search filter.</p>
            <button className="btn-clear-search" onClick={() => setSearchQuery('')}>
              CLEAR SEARCH FILTER
            </button>
          </div>
        ) : (
          <div className="all-areas-grid">
            {filteredAreas.map((area) => {
              const counts = getAreaCounts(area, listings);

              return (
                <div 
                  className="area-card" 
                  key={area.id}
                  onClick={() => handleAreaClick(area)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="area-image-wrapper">
                    <img src={ListingService.formatImageUrl(area.imageUrl)} alt={area.title} className="area-image" />
                    <div className="area-badges-overlay">
                      <span className="area-badge area-badge-sale">
                        <i className="bi bi-tag-fill"></i> {counts.sale} For Sale
                      </span>
                      <span className="area-badge area-badge-rent">
                        <i className="bi bi-key-fill"></i> {counts.rent} For Rent
                      </span>
                    </div>
                  </div>
                <div className="area-card-body">
                  <h3 className="area-title">{area.title}</h3>
                  <ul className="area-highlights">
                    {area.highlights && area.highlights.map((highlight, idx) => (
                      <li key={idx}>
                        <i className="bi bi-check-circle-fill"></i>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                  <button 
                    className="btn-area-view"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAreaClick(area);
                    }}
                  >
                    VIEW PROPERTIES
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllAreasPage;
